import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/interval';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/operator/mapTo';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/throttleTime';
import 'rxjs/add/operator/switchMap';
import type { IScheduler } from 'rxjs/Scheduler';
import getVisibility$ from '@atlassian/jira-common-page-visiblity-stream/src/page-visibility-stream';
import { getBacklogPollingInterval } from '../../feature-flags';
import { REFRESH_SOURCE_ACTIVE_TAB, REFRESH_SOURCE_POLLING } from '../../model/constants';
import { refreshDataRequest } from '../../state/actions/app';
import { getIsCompanyManaged } from '../../state/selectors/app';
import { hasAnyActiveOptimistic } from '../../state/selectors/optimistic';
import type { MiddlewareAPI, ActionsObservable, Action } from '../../state/types';

/**
 * Start with marker, so that we guarantee there are no
 * polls or visibility changed refresh actions emitted
 * without throttling.
 */
export const STREAM_START_MARKER = Symbol('STREAM_START_MARKER');
const DEBOUNCE_TIME = 1000;

export const refreshBacklog =
	(
		debounceScheduler?: IScheduler,
		intervalScheduler?: IScheduler,
		throttleScheduler?: IScheduler,
	) =>
	(action$: ActionsObservable, store: MiddlewareAPI) => {
		const pollingInterval = getBacklogPollingInterval();

		return getVisibility$()
			.distinctUntilChanged()
			.debounceTime(DEBOUNCE_TIME, debounceScheduler) // prevent multiple requests when switching tabs within 1s
			.switchMap((isTabVisible, idx) => {
				if (!isTabVisible) {
					return Observable.empty<never>();
				}

				const refreshStream$: Observable<Action | typeof STREAM_START_MARKER> = Observable.interval(
					pollingInterval,
					intervalScheduler,
				)
					.mapTo(refreshDataRequest(REFRESH_SOURCE_POLLING))
					.filter(() => isTabVisible)
					.filter(() => !hasAnyActiveOptimistic(store.getState()))
					.filter(() => getIsCompanyManaged(store.getState()));

				return idx === 0
					? refreshStream$.startWith(STREAM_START_MARKER)
					: refreshStream$.startWith(refreshDataRequest(REFRESH_SOURCE_ACTIVE_TAB));
			})
			.throttleTime(pollingInterval, throttleScheduler)
			.filter(
				(action: Action | typeof STREAM_START_MARKER): action is Action =>
					action !== STREAM_START_MARKER,
			);
	};
